// MyCam 手機攝影機（型號 ipcam-phone，ls-loop docs/mycam）在網頁觀看的差異（MW；對齊 Android 觀看 App 的 PhoneCamText.kt / CamTz.kt）。
// - 沒有 SD 卡：錄影存在手機自己的空間 → 同一套「SD 卡」畫面照用，只把字換掉。
// - 沒有的硬體 / 指令（重開機、回復原廠、Wi-Fi 訊號、溫度、外部備份、卸載 / 格式化）不顯示。
// - 時區跟著那支手機（signin 帶上來），不能從這裡改。
import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { TIMEZONES } from '../features/devices/settings/timezones';

export const PHONE_CAM_MODEL = 'ipcam-phone';

export function isPhoneCam(model?: string | null): boolean {
  return model === PHONE_CAM_MODEL;
}

/** 「SD 卡 / 記憶卡」→「手機空間」（依目前語系的對照）。不是手機攝影機就原樣回。 */
export function phoneize(text: string, phone: boolean): string {
  if (!phone || !text) return text;
  const terms = i18n.t('phonecam.sdTerms', { returnObjects: true }) as unknown;
  const to = i18n.t('phonecam.storage');
  if (!Array.isArray(terms)) return text;
  return (terms as string[]).reduce((acc, from) => acc.split(from).join(to), text);
}

/** 裝置 POSIX → IANA（顯示用；同 Android DeviceTimezones.javaZone）。表內有 → IANA；沒有 → 標準時差的 Etc/GMT±N。 */
export function ianaForPosix(posix?: string | null): string | null {
  const p = (posix ?? '').trim();
  if (!p) return null;
  const hit = TIMEZONES.find((o) => o.posix === p);
  if (hit) return hit.iana;
  const m = /^(?:<[^>]+>|[A-Za-z]{3,})([+-]?)(\d{1,2})(?::(\d{2}))?/.exec(p);
  if (!m || m[3]) return null; // 半小時時差沒有 Etc/ 名稱 → 交給呼叫端用瀏覽器時區
  const h = Number(m[2]);
  if (h > 14) return null;
  // POSIX 的符號跟 GMT 相反（CST-8 = GMT+8）；Etc/GMT 又跟 GMT 相反（Etc/GMT-8 = GMT+8）→ 剛好跟 POSIX 同號
  return h === 0 ? 'Etc/UTC' : `Etc/GMT${m[1] === '-' ? '-' : '+'}${h}`;
}

/** 時差標示「GMT+8」「GMT+5:30」（這個時區在 atMs 的時差）。 */
export function offsetLabel(iana: string, atMs: number = Date.now()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'shortOffset' }).formatToParts(new Date(atMs));
    const name = parts.find((x) => x.type === 'timeZoneName')?.value ?? '';
    return name === 'GMT' ? 'GMT+0' : name;
  } catch {
    return iana;
  }
}

/** 這一頁是不是手機攝影機（裝置頁 / 回放頁在最外層 Provider）。 */
export const PhoneCamContext = createContext(false);

/** 同 useTranslation()；在手機攝影機的頁面裡，t() 出來的字自動把「SD 卡」換成「手機空間」。 */
export function usePhoneT(): ReturnType<typeof useTranslation> {
  const r = useTranslation();
  const phone = useContext(PhoneCamContext);
  // 要 memo：呼叫端常把 t 放進 useCallback / useEffect 依賴，每次 render 換新會變成無限重抓
  const t = useMemo(
    () => ((...args: unknown[]) => phoneize(String((r.t as unknown as (...a: unknown[]) => unknown)(...args)), true)) as unknown as typeof r.t,
    [r.t]
  );
  if (!phone) return r;
  return Object.assign([t, r.i18n, r.ready], { t, i18n: r.i18n, ready: r.ready }) as unknown as ReturnType<typeof useTranslation>;
}
